/**
 * ADMIN DASHBOARD
 * 
 * Central hub for database maintenance and data quality tools.
 * 
 * Features:
 * - Data quality overview (stats on missing fields, duplicates, etc.)
 * - Similar brand name detector (find typos/variations)
 * - Similar item name detector within brands
 * - Items missing key fields
 * - Quick links to other admin tools
 * 
 * REQUIRES: isAdmin: true on your user document in Firestore
 * 
 * SETUP: Add route to App.tsx:
 * import AdminDashboard from './pages/AdminDashboard';
 * <Route path="/admin" element={<AdminDashboard />} />
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collectionGroup,
  writeBatch,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Shield,
  AlertTriangle,
  Building2,
  CheckCircle,
  Search,
  Database,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Merge,
  Package,
  Castle,
  Sparkles,
  FileWarning,
  X,
  Eye,
  EyeOff,
  Wand2,
  ArrowUpDown
} from 'lucide-react';
import { normalizeTypography } from '../utils/normalizeTypography';

// Simple string similarity (Levenshtein-ish)
function similarity(s1: string, s2: string): number {
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  
  if (longer.length === 0) return 1.0;
  
  const costs: number[] = [];
  for (let i = 0; i <= longer.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= shorter.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (longer.charAt(i - 1) !== shorter.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[shorter.length] = lastValue;
  }
  
  return (longer.length - costs[shorter.length]) / longer.length;
}

// Normalize string for comparison
function normalize(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
}

interface DataQualityStats {
  totalItems: number;
  totalBrands: number;
  totalUsers: number;
  itemsMissingBrand: number;
  itemsMissingName: number;
  itemsMissingReleaseInfo: number;
  itemsMissingPhotos: number;
}

interface SimilarPair {
  name1: string;
  name2: string;
  similarity: number;
  count1?: number;
  count2?: number;
}

interface MissingFieldItem {
  id: string;
  userId: string;
  name: string;
  brand: string;
  missingFields: string[];
}

export default function AdminDashboard() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  
  // Data quality stats
  const [stats, setStats] = useState<DataQualityStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Similar brands
  const [similarBrands, setSimilarBrands] = useState<SimilarPair[]>([]);
  const [loadingSimilarBrands, setLoadingSimilarBrands] = useState(false);
  const [showSimilarBrands, setShowSimilarBrands] = useState(false);
  
  // Items missing fields
  const [missingFieldItems, setMissingFieldItems] = useState<MissingFieldItem[]>([]);
  const [loadingMissingFields, setLoadingMissingFields] = useState(false);
  const [showMissingFields, setShowMissingFields] = useState(false);
  
  // Similar item names within brands
  const [similarItems, setSimilarItems] = useState<{brand: string, pairs: SimilarPair[]}[]>([]);
  const [loadingSimilarItems, setLoadingSimilarItems] = useState(false);
  const [showSimilarItems, setShowSimilarItems] = useState(false);

  // Catalog merge candidates — polishCatalog entries with the lowest community
  // confidence (confirmCount asc, then collectorCount asc). Surfaces typos /
  // orphan entries that haven't been confirmed by anyone in the community.
  type CatalogCandidate = { slug: string; brand: string; name: string; collectorCount: number; confirmCount: number };
  const [catalogCandidates, setCatalogCandidates] = useState<CatalogCandidate[]>([]);
  const [loadingCatalogCandidates, setLoadingCatalogCandidates] = useState(false);
  const [showCatalogCandidates, setShowCatalogCandidates] = useState(false);
  
  // Dismissed pairs (stored in Firestore)
  const [dismissedBrandPairs, setDismissedBrandPairs] = useState<Set<string>>(new Set());
  const [dismissedItemPairs, setDismissedItemPairs] = useState<Set<string>>(new Set());
  const [showDismissed, setShowDismissed] = useState(false);

  // Bulk-select state for Similar Items. Stores pairKeys for each pair the
  // admin has ticked. When >0, the panel shows a "Dismiss N selected" bar
  // so 6000-row cleanup passes don't require one-click-per-pair.
  const [selectedItemPairs, setSelectedItemPairs] = useState<Set<string>>(new Set());
  const [isBulkDismissing, setIsBulkDismissing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  // Typography backfill state — for the one-shot Cloud Function that
  // rewrites smart quotes/dashes across every item + brand + polishCatalog
  // doc. Result blob is rendered as pretty JSON so a dry run's sampleChanges
  // is legible before committing.
  const [typographyBackfillLoading, setTypographyBackfillLoading] = useState<'idle' | 'dry' | 'real'>('idle');
  const [typographyBackfillResult, setTypographyBackfillResult] = useState<any>(null);
  const [currencyBackfillLoading, setCurrencyBackfillLoading] = useState<'idle' | 'dry' | 'real'>('idle');
  const [currencyBackfillResult, setCurrencyBackfillResult] = useState<any>(null);
  
  // Merge items modal
  const [showMergeItemsModal, setShowMergeItemsModal] = useState(false);
  const [mergeItemBrand, setMergeItemBrand] = useState('');
  const [mergeItemOld, setMergeItemOld] = useState('');
  const [mergeItemNew, setMergeItemNew] = useState('');
  const [mergeItemStatus, setMergeItemStatus] = useState('');
  const [isMergingItems, setIsMergingItems] = useState(false);

  // Pending brand claims count
  const [pendingClaimsCount, setPendingClaimsCount] = useState(0);
  // Pending moderation reports count — admins need to triage within 24h for
  // App Store compliance, so the badge surfaces volume at a glance.
  const [pendingReportsCount, setPendingReportsCount] = useState(0);

  // Check admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data()?.isAdmin === true);
        }
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsubscribe();
  }, []);

  // Load dismissed pairs from Firestore. Gated on isAdmin so the read only
  // fires after auth resolves — otherwise it can race against the auth-state
  // listener and hit the rules with an unauthenticated request, silently
  // returning an empty set and looking like the tracking is broken.
  useEffect(() => {
    if (!isAdmin) return;
    const loadDismissals = async () => {
      try {
        const brandDismissalsSnap = await getDocs(collection(db, 'adminDismissals', 'brands', 'pairs'));
        const brandSet = new Set<string>();
        brandDismissalsSnap.docs.forEach(doc => brandSet.add(doc.id));
        setDismissedBrandPairs(brandSet);

        const itemDismissalsSnap = await getDocs(collection(db, 'adminDismissals', 'items', 'pairs'));
        const itemSet = new Set<string>();
        itemDismissalsSnap.docs.forEach(doc => itemSet.add(doc.id));
        setDismissedItemPairs(itemSet);
      } catch (error) {
        console.error('Error loading dismissals:', error);
        flashFeedback(`Couldn't load dismissals from Firestore: ${(error as Error).message}`, 'err');
      }
    };
    loadDismissals();
  }, [isAdmin]);

  // Load pending brand claims count
  useEffect(() => {
    const loadPendingClaims = async () => {
      try {
        const q = query(collection(db, 'brands'), where('status', '==', 'claimed'));
        const snapshot = await getDocs(q);
        setPendingClaimsCount(snapshot.size);
      } catch (error) {
        console.error('Error loading pending claims:', error);
      }
    };
    if (isAdmin) {
      loadPendingClaims();
    }
  }, [isAdmin]);

  // Load pending moderation reports count for the badge.
  useEffect(() => {
    const loadPendingReports = async () => {
      try {
        const q = query(collection(db, 'reports'), where('status', '==', 'pending'));
        const snapshot = await getDocs(q);
        setPendingReportsCount(snapshot.size);
      } catch (error) {
        console.error('Error loading pending reports:', error);
      }
    };
    if (isAdmin) loadPendingReports();
  }, [isAdmin]);

  // Transient banner shown after dismiss/undismiss so a Firestore failure
  // doesn't stay silent in the console. `kind` drives the color.
  const [dismissalFeedback, setDismissalFeedback] = useState<
    { text: string; kind: 'ok' | 'err' } | null
  >(null);
  const flashFeedback = (text: string, kind: 'ok' | 'err') => {
    setDismissalFeedback({ text, kind });
    // Auto-clear after 3 seconds so successive dismisses don't stack banners.
    setTimeout(() => setDismissalFeedback(null), 3000);
  };

  // Create a Firestore-safe, case-and-punctuation-invariant key for a pair.
  // Uses `normalize()` (lowercase + strip non-alphanumeric) on brand + names
  // so:
  //   - Dismissing ("Moon Cat", "Mooncat") also dismisses ("moon cat", "MOONCAT")
  //     — the tool otherwise re-surfaces the "same" pair whenever a new case
  //     variant appears.
  //   - Brands/names containing `/` won't ever produce an illegal Firestore
  //     doc id — `normalize()` strips those chars entirely.
  // Historical note: this replaces the raw-name-based key that was silently
  // failing under Firestore's doc-id character rules and case sensitivity.
  const pairKey = (name1: string, name2: string, brand?: string): string => {
    const n1 = normalize(name1);
    const n2 = normalize(name2);
    const sorted = [n1, n2].sort();
    const b = brand ? normalize(brand) : '';
    return brand ? `${b}|${sorted[0]}|${sorted[1]}` : `${sorted[0]}|${sorted[1]}`;
  };

  // Dismiss a brand pair
  const dismissBrandPair = async (name1: string, name2: string) => {
    const key = pairKey(name1, name2);
    try {
      await setDoc(doc(db, 'adminDismissals', 'brands', 'pairs', key), {
        name1,
        name2,
        dismissedAt: new Date(),
        dismissedBy: currentUser
      });
      setDismissedBrandPairs(prev => new Set([...prev, key]));
      flashFeedback(`Dismissed "${name1}" ↔ "${name2}" — won't show again.`, 'ok');
    } catch (error) {
      console.error('Error dismissing pair:', error);
      flashFeedback(`Couldn't save dismissal: ${(error as Error).message}`, 'err');
    }
  };

  // Undismiss a brand pair
  const undismissBrandPair = async (name1: string, name2: string) => {
    const key = pairKey(name1, name2);
    try {
      await deleteDoc(doc(db, 'adminDismissals', 'brands', 'pairs', key));
      setDismissedBrandPairs(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
      flashFeedback(`Restored "${name1}" ↔ "${name2}" to the list.`, 'ok');
    } catch (error) {
      console.error('Error undismissing pair:', error);
      flashFeedback(`Couldn't restore: ${(error as Error).message}`, 'err');
    }
  };

  // Dismiss an item pair
  const dismissItemPair = async (brand: string, name1: string, name2: string) => {
    const key = pairKey(name1, name2, brand);
    try {
      await setDoc(doc(db, 'adminDismissals', 'items', 'pairs', key), {
        brand,
        name1,
        name2,
        dismissedAt: new Date(),
        dismissedBy: currentUser
      });
      setDismissedItemPairs(prev => new Set([...prev, key]));
      flashFeedback(`Dismissed "${name1}" ↔ "${name2}" for ${brand} — won't show again.`, 'ok');
    } catch (error) {
      console.error('Error dismissing pair:', error);
      flashFeedback(`Couldn't save dismissal: ${(error as Error).message}`, 'err');
    }
  };

  // Undismiss an item pair
  const undismissItemPair = async (brand: string, name1: string, name2: string) => {
    const key = pairKey(name1, name2, brand);
    try {
      await deleteDoc(doc(db, 'adminDismissals', 'items', 'pairs', key));
      setDismissedItemPairs(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
      flashFeedback(`Restored "${name1}" ↔ "${name2}" to the list.`, 'ok');
    } catch (error) {
      console.error('Error undismissing pair:', error);
      flashFeedback(`Couldn't restore: ${(error as Error).message}`, 'err');
    }
  };

  // Toggle a single pair's selection state for the bulk-dismiss flow.
  const toggleItemPairSelection = (brand: string, name1: string, name2: string) => {
    const key = pairKey(name1, name2, brand);
    setSelectedItemPairs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Select every visible (non-dismissed) pair inside one brand group. Called
  // by the per-brand-group "Select all" checkbox so cleanup passes on a single
  // brand don't require 500 individual ticks.
  const toggleSelectAllInBrandGroup = (brandGroup: { brand: string; pairs: SimilarPair[] }) => {
    const visibleKeys = brandGroup.pairs
      .filter(p => !dismissedItemPairs.has(pairKey(p.name1, p.name2, brandGroup.brand)))
      .map(p => pairKey(p.name1, p.name2, brandGroup.brand));
    const allSelected = visibleKeys.length > 0 && visibleKeys.every(k => selectedItemPairs.has(k));
    setSelectedItemPairs(prev => {
      const next = new Set(prev);
      if (allSelected) visibleKeys.forEach(k => next.delete(k));
      else visibleKeys.forEach(k => next.add(k));
      return next;
    });
  };

  // Select every visible (non-dismissed) pair across ALL brand groups.
  const toggleSelectAllVisible = () => {
    const allVisibleKeys: string[] = [];
    for (const bg of similarItems) {
      for (const p of bg.pairs) {
        const k = pairKey(p.name1, p.name2, bg.brand);
        if (!dismissedItemPairs.has(k)) allVisibleKeys.push(k);
      }
    }
    const allSelected = allVisibleKeys.length > 0 && allVisibleKeys.every(k => selectedItemPairs.has(k));
    setSelectedItemPairs(allSelected ? new Set() : new Set(allVisibleKeys));
  };

  // Bulk-dismiss every selected pair. Firestore's writeBatch caps at 500 ops
  // per commit — chunk into 400-op batches (buffer for retries) and progress
  // through them so a 6000-row pass doesn't fail on batch #13.
  const bulkDismissSelected = async () => {
    if (selectedItemPairs.size === 0 || isBulkDismissing) return;

    // Look up brand/name1/name2 for each selected key by walking similarItems.
    const toDismiss: { key: string; brand: string; name1: string; name2: string }[] = [];
    for (const bg of similarItems) {
      for (const p of bg.pairs) {
        const k = pairKey(p.name1, p.name2, bg.brand);
        if (selectedItemPairs.has(k)) {
          toDismiss.push({ key: k, brand: bg.brand, name1: p.name1, name2: p.name2 });
        }
      }
    }

    if (toDismiss.length === 0) return;

    setIsBulkDismissing(true);
    setBulkProgress({ done: 0, total: toDismiss.length });

    const BATCH_SIZE = 400;
    const now = new Date();
    let failed = 0;
    try {
      for (let i = 0; i < toDismiss.length; i += BATCH_SIZE) {
        const chunk = toDismiss.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const p of chunk) {
          batch.set(doc(db, 'adminDismissals', 'items', 'pairs', p.key), {
            brand: p.brand,
            name1: p.name1,
            name2: p.name2,
            dismissedAt: now,
            dismissedBy: currentUser,
          });
        }
        try {
          await batch.commit();
        } catch (batchErr) {
          console.error(`Batch ${i / BATCH_SIZE + 1} failed:`, batchErr);
          failed += chunk.length;
        }
        setBulkProgress({ done: Math.min(i + chunk.length, toDismiss.length), total: toDismiss.length });
      }

      // Local state — only reflect the ones that presumably succeeded.
      const succeededKeys = toDismiss.slice(0, toDismiss.length - failed).map(p => p.key);
      setDismissedItemPairs(prev => new Set([...prev, ...succeededKeys]));
      setSelectedItemPairs(new Set());

      if (failed === 0) {
        flashFeedback(`Dismissed ${toDismiss.length} pairs.`, 'ok');
      } else {
        flashFeedback(
          `Dismissed ${toDismiss.length - failed} pairs; ${failed} failed. Check console for details.`,
          'err'
        );
      }
    } finally {
      setIsBulkDismissing(false);
      setBulkProgress(null);
    }
  };

  // Trigger the backfillTypographyNormalization Cloud Function. Prompts
  // for ADMIN_SECRET on first call this session and caches it in
  // sessionStorage so subsequent dry/real runs don't re-prompt. Uses the
  // same secret-in-query-param pattern as the other backfill functions
  // (backfillBrandLower, backfillItemSlugs, etc.) — no auth token
  // needed because ADMIN_SECRET is verified server-side inside the
  // function against Firebase's defineSecret store.
  const runTypographyBackfill = async (dryRun: boolean) => {
    let secret = sessionStorage.getItem('adminSecret');
    if (!secret) {
      secret = window.prompt('Paste ADMIN_SECRET (cached for this session):') || '';
      if (!secret) return;
      sessionStorage.setItem('adminSecret', secret);
    }
    if (!dryRun) {
      const ok = window.confirm(
        'Rewrite smart-quote / typographic characters across all items, brands, and polishCatalog docs. ' +
          'This is idempotent (re-runs are safe) but writes to production data. Continue?'
      );
      if (!ok) return;
    }

    setTypographyBackfillLoading(dryRun ? 'dry' : 'real');
    setTypographyBackfillResult(null);
    try {
      const url = `https://us-central1-polish-all-the-things--app.cloudfunctions.net/backfillTypographyNormalization?secret=${encodeURIComponent(secret)}${dryRun ? '&dryRun=true' : ''}`;
      const res = await fetch(url);
      const json = await res.json();
      if (res.status === 401) {
        // Wrong secret — clear cache so the next click re-prompts.
        sessionStorage.removeItem('adminSecret');
      }
      setTypographyBackfillResult({ status: res.status, ...json });
    } catch (err) {
      setTypographyBackfillResult({ error: String(err) });
    } finally {
      setTypographyBackfillLoading('idle');
    }
  };

  // Trigger the backfillItemCurrency Cloud Function. Same shape as the
  // typography backfill — reuses the sessionStorage ADMIN_SECRET cache
  // so the operator doesn't re-enter it for every backfill button.
  const runCurrencyBackfill = async (dryRun: boolean) => {
    let secret = sessionStorage.getItem('adminSecret');
    if (!secret) {
      secret = window.prompt('Paste ADMIN_SECRET (cached for this session):') || '';
      if (!secret) return;
      sessionStorage.setItem('adminSecret', secret);
    }
    if (!dryRun) {
      const ok = window.confirm(
        'Stamp priceCurrency = owner\'s profile currency on every user item that has a `price` but no `priceCurrency`. ' +
          'Idempotent (re-runs are safe). Continue?'
      );
      if (!ok) return;
    }

    setCurrencyBackfillLoading(dryRun ? 'dry' : 'real');
    setCurrencyBackfillResult(null);
    try {
      const url = `https://us-central1-polish-all-the-things--app.cloudfunctions.net/backfillItemCurrency?secret=${encodeURIComponent(secret)}${dryRun ? '&dryRun=true' : ''}`;
      const res = await fetch(url);
      const json = await res.json();
      if (res.status === 401) sessionStorage.removeItem('adminSecret');
      setCurrencyBackfillResult({ status: res.status, ...json });
    } catch (err) {
      setCurrencyBackfillResult({ error: String(err) });
    } finally {
      setCurrencyBackfillLoading('idle');
    }
  };

  // Open merge modal for items
  const openMergeItemsModal = (brand: string, name1: string, name2: string, count1: number, count2: number) => {
    setMergeItemBrand(brand);
    // Default: keep the one with more items as the "TO" (canonical) name.
    // ALSO normalize typography on the TO field so the merge result uses
    // plain ASCII punctuation ("Lil' Sweet" not "Lil’ Sweet"). Common
    // culprit: iOS auto-corrects `'` to `’` on typing, so users on iOS
    // and Android end up with visually-identical polish names that
    // Firestore treats as different strings.
    if (count1 >= count2) {
      setMergeItemNew(normalizeTypography(name1));
      setMergeItemOld(name2);
    } else {
      setMergeItemNew(normalizeTypography(name2));
      setMergeItemOld(name1);
    }
    setMergeItemStatus('');
    setShowMergeItemsModal(true);
  };

  // Merge items (rename old to new across all users)
  const mergeItems = async () => {
    if (!mergeItemBrand || !mergeItemOld || !mergeItemNew) {
      setMergeItemStatus('Please fill in all fields');
      return;
    }
    
    if (mergeItemOld === mergeItemNew) {
      setMergeItemStatus('Names are the same!');
      return;
    }
    
    setIsMergingItems(true);
    setMergeItemStatus('Finding items to update...');
    
    try {
      // Find all items with the old name for this brand
      const itemsSnapshot = await getDocs(collectionGroup(db, 'items'));
      
      const itemsToUpdate: any[] = [];
      itemsSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.brand === mergeItemBrand && data.name === mergeItemOld) {
          itemsToUpdate.push(docSnap.ref);
        }
      });
      
      if (itemsToUpdate.length === 0) {
        setMergeItemStatus('No items found with that name');
        setIsMergingItems(false);
        return;
      }
      
      setMergeItemStatus(`Updating ${itemsToUpdate.length} items...`);
      
      // Update in batches
      let updated = 0;
      let batch = writeBatch(db);
      let batchCount = 0;
      
      for (const ref of itemsToUpdate) {
        batch.update(ref, { name: mergeItemNew });
        batchCount++;
        
        if (batchCount >= 450) {
          await batch.commit();
          updated += batchCount;
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
        updated += batchCount;
      }
      
      setMergeItemStatus(`✅ Successfully merged ${updated} items!`);
      
      // Dismiss this pair since it's now merged
      await dismissItemPair(mergeItemBrand, mergeItemOld, mergeItemNew);
      
      // Update the similar items list
      setSimilarItems(prev => prev.map(brandGroup => {
        if (brandGroup.brand === mergeItemBrand) {
          return {
            ...brandGroup,
            pairs: brandGroup.pairs.filter(p => 
              !(p.name1 === mergeItemOld || p.name2 === mergeItemOld)
            )
          };
        }
        return brandGroup;
      }).filter(bg => bg.pairs.length > 0));
      
    } catch (error: any) {
      setMergeItemStatus(`❌ Error: ${error.message}`);
    }
    
    setIsMergingItems(false);
  };

  // Load data quality stats
  const loadStats = async () => {
    setLoadingStats(true);
    try {
      // Get all items
      const itemsSnapshot = await getDocs(collectionGroup(db, 'items'));
      
      // Get all brands
      const brandsSnapshot = await getDocs(collection(db, 'brands'));
      
      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      
      let missingBrand = 0;
      let missingName = 0;
      let missingReleaseInfo = 0;
      let missingPhotos = 0;
      
      itemsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.brand || data.brand.trim() === '') missingBrand++;
        if (!data.name || data.name.trim() === '') missingName++;
        if (!data.releaseType || data.releaseType === 'unknown') missingReleaseInfo++;
        if (!data.photos || data.photos.length === 0) missingPhotos++;
      });
      
      setStats({
        totalItems: itemsSnapshot.size,
        totalBrands: brandsSnapshot.size,
        totalUsers: usersSnapshot.size,
        itemsMissingBrand: missingBrand,
        itemsMissingName: missingName,
        itemsMissingReleaseInfo: missingReleaseInfo,
        itemsMissingPhotos: missingPhotos
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
    setLoadingStats(false);
  };

  // Find similar brand names
  const findSimilarBrands = async () => {
    setLoadingSimilarBrands(true);
    setSimilarBrands([]);
    
    try {
      const brandsSnapshot = await getDocs(collection(db, 'brands'));
      const brands = brandsSnapshot.docs.map(doc => ({
        name: doc.data().name,
        count: doc.data().stats?.totalItems || 0
      }));
      
      const pairs: SimilarPair[] = [];
      
      // Compare each brand to every other brand
      for (let i = 0; i < brands.length; i++) {
        for (let j = i + 1; j < brands.length; j++) {
          const norm1 = normalize(brands[i].name);
          const norm2 = normalize(brands[j].name);
          
          // Skip if normalized versions are identical (exact duplicates)
          if (norm1 === norm2) {
            pairs.push({
              name1: brands[i].name,
              name2: brands[j].name,
              similarity: 1.0,
              count1: brands[i].count,
              count2: brands[j].count
            });
            continue;
          }
          
          const sim = similarity(norm1, norm2);
          
          // Flag if > 80% similar but not identical
          if (sim > 0.8 && sim < 1.0) {
            pairs.push({
              name1: brands[i].name,
              name2: brands[j].name,
              similarity: sim,
              count1: brands[i].count,
              count2: brands[j].count
            });
          }
        }
      }
      
      // Sort by similarity (highest first)
      pairs.sort((a, b) => b.similarity - a.similarity);
      setSimilarBrands(pairs);
      setShowSimilarBrands(true);
    } catch (error) {
      console.error('Error finding similar brands:', error);
    }
    
    setLoadingSimilarBrands(false);
  };

  // Find items missing key fields
  const findMissingFields = async () => {
    setLoadingMissingFields(true);
    setMissingFieldItems([]);
    
    try {
      const itemsSnapshot = await getDocs(collectionGroup(db, 'items'));
      const items: MissingFieldItem[] = [];
      
      itemsSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const missing: string[] = [];
        
        if (!data.brand || data.brand.trim() === '') missing.push('brand');
        if (!data.name || data.name.trim() === '') missing.push('name');
        if (!data.releaseType || data.releaseType === 'unknown') missing.push('releaseType');
        if (!data.photos || data.photos.length === 0) missing.push('photos');
        
        // Only include items missing important fields (not just photos)
        if (missing.includes('brand') || missing.includes('name')) {
          items.push({
            id: docSnap.id,
            userId: docSnap.ref.parent.parent?.id || 'unknown',
            name: data.name || '(no name)',
            brand: data.brand || '(no brand)',
            missingFields: missing
          });
        }
      });
      
      setMissingFieldItems(items);
      setShowMissingFields(true);
    } catch (error) {
      console.error('Error finding missing fields:', error);
    }
    
    setLoadingMissingFields(false);
  };

  // Find similar item names within brands
  const findCatalogCandidates = async () => {
    setLoadingCatalogCandidates(true);
    setShowCatalogCandidates(true);
    try {
      // Fetch a bounded slice of the catalog ordered by collectorCount asc — the
      // entries most likely to be typos/orphans float to the top. Re-sort
      // client-side by confirmCount asc as the primary signal so unconfirmed
      // entries appear first regardless of who else fetched the page.
      const snap = await getDocs(
        query(collection(db, 'polishCatalog'), orderBy('collectorCount', 'asc'), limit(200))
      );
      const rows: CatalogCandidate[] = snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        return {
          slug: d.id,
          brand: (data.brand as string) || '(no brand)',
          name: (data.name as string) || '(no name)',
          collectorCount: (data.collectorCount as number) ?? 0,
          confirmCount: (data.confirmCount as number) ?? 0,
        };
      });
      rows.sort(
        (a, b) =>
          a.confirmCount - b.confirmCount ||
          a.collectorCount - b.collectorCount ||
          a.name.localeCompare(b.name)
      );
      setCatalogCandidates(rows.slice(0, 100));
    } finally {
      setLoadingCatalogCandidates(false);
    }
  };

  const findSimilarItems = async () => {
    setLoadingSimilarItems(true);
    setSimilarItems([]);
    
    try {
      const itemsSnapshot = await getDocs(collectionGroup(db, 'items'));
      
      // Group items by brand
      const brandItems = new Map<string, Map<string, number>>();
      
      itemsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.brand || !data.name) return;
        
        const brand = data.brand;
        const name = data.name;
        
        if (!brandItems.has(brand)) {
          brandItems.set(brand, new Map());
        }
        
        const itemMap = brandItems.get(brand)!;
        itemMap.set(name, (itemMap.get(name) || 0) + 1);
      });
      
      const results: {brand: string, pairs: SimilarPair[]}[] = [];
      
      // For each brand, compare item names
      brandItems.forEach((itemMap, brand) => {
        const items = Array.from(itemMap.entries());
        const pairs: SimilarPair[] = [];
        
        for (let i = 0; i < items.length; i++) {
          for (let j = i + 1; j < items.length; j++) {
            const norm1 = normalize(items[i][0]);
            const norm2 = normalize(items[j][0]);
            
            if (norm1 === norm2) {
              // Exact match after normalization
              pairs.push({
                name1: items[i][0],
                name2: items[j][0],
                similarity: 1.0,
                count1: items[i][1],
                count2: items[j][1]
              });
            } else {
              const sim = similarity(norm1, norm2);
              if (sim > 0.85) {
                pairs.push({
                  name1: items[i][0],
                  name2: items[j][0],
                  similarity: sim,
                  count1: items[i][1],
                  count2: items[j][1]
                });
              }
            }
          }
        }
        
        if (pairs.length > 0) {
          pairs.sort((a, b) => b.similarity - a.similarity);
          results.push({ brand, pairs });
        }
      });
      
      // Sort by number of issues
      results.sort((a, b) => b.pairs.length - a.pairs.length);
      setSimilarItems(results);
      setShowSimilarItems(true);
    } catch (error) {
      console.error('Error finding similar items:', error);
    }
    
    setLoadingSimilarItems(false);
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-400" />
          <p className="text-glass-secondary">Checking admin status...</p>
        </div>
      </div>
    );
  }

  if (!currentUser || !isAdmin) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold text-glass mb-2 font-heading">Admin Access Required</h1>
          <p className="text-glass-secondary font-body">
            You must be logged in as an admin to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      {/* Floating dismissal feedback — surfaces Firestore ok/err so a
          persistence problem isn't hidden in the console. Fixed at the
          top-right of the viewport so it doesn't push other UI around. */}
      {dismissalFeedback && (
        <div
          className={`fixed top-4 right-4 z-50 max-w-md px-4 py-3 rounded-lg shadow-lg font-body text-sm ${
            dismissalFeedback.kind === 'ok'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {dismissalFeedback.text}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center gap-3">
          <Shield className="w-10 h-10" />
          <div>
            <h1 className="text-2xl font-bold font-heading">Admin Dashboard</h1>
            <p className="opacity-90 font-body">Database maintenance & data quality tools</p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        <Link
          to="/admin/cleanup"
          className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl shadow p-4 hover:shadow-lg transition-shadow flex items-center gap-3 text-white"
        >
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
            <Wand2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-bold font-body">Cleanup Wizard</div>
            <div className="text-sm opacity-90 font-body">Fix data quality issues</div>
          </div>
        </Link>

        <Link
          to="/admin/populate-brands"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
            <Castle className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Populate Brands</div>
            <div className="text-sm text-glass-secondary font-body">Create brand pages from items</div>
          </div>
        </Link>

        <Link
          to="/admin/merge-brands"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Merge Brands</div>
            <div className="text-sm text-glass-secondary font-body">Fix duplicate/misspelled brands</div>
          </div>
        </Link>

        <Link
          to="/admin/collections-cleanup"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-teal-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Collections Cleanup</div>
            <div className="text-sm text-glass-secondary font-body">Merge variant collection names or move tag-like values into tags</div>
          </div>
        </Link>

        <Link
          to="/admin/polish-names-cleanup"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-fuchsia-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-fuchsia-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Polish Names Cleanup</div>
            <div className="text-sm text-glass-secondary font-body">Bulk-fix polish name variants within a brand — cascades to items, confirms, creations</div>
          </div>
        </Link>

        <Link
          to="/admin/tag-pusher"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-sky-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-sky-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Tag Pusher</div>
            <div className="text-sm text-glass-secondary font-body">Add tags to items whose name contains a substring — works across all 8 item types</div>
          </div>
        </Link>

        <Link
          to="/admin/assign-collections"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Assign Collections</div>
            <div className="text-sm text-glass-secondary font-body">Fill in collectionName on user items sitting in Uncategorized / Core / Permanent</div>
          </div>
        </Link>

        <Link
          to="/admin/migrate-event-source"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-teal-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Migrate eventSource → eventName</div>
            <div className="text-sm text-glass-secondary font-body">Backfill the Jan 2026 field rename for user items still on the legacy field</div>
          </div>
        </Link>

        <Link
          to="/admin/backfill-shipping"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Backfill shipping + currency</div>
            <div className="text-sm text-glass-secondary font-body">Convert legacy free-text location / shipsTo into structured country + currency fields</div>
          </div>
        </Link>


        <Link
          to="/admin/set-brand-curation"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Set Brand Curation</div>
            <div className="text-sm text-glass-secondary font-body">Batch-mark brands as verified / community / needs-review</div>
          </div>
        </Link>

        <Link
          to="/admin/merge-brand-docs"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-rose-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-rose-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Merge Brand Docs by ID</div>
            <div className="text-sm text-glass-secondary font-body">Merge duplicates with auto-IDs or identical names</div>
          </div>
        </Link>

        <Link
          to="/admin/fix-orphan-catalog"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3"
        >
          <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
            <Merge className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Fix Orphan Catalog</div>
            <div className="text-sm text-glass-secondary font-body">Map polishCatalog orphans → canonical brands</div>
          </div>
        </Link>

        <Link
          to="/admin/brand-claims"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3 relative"
        >
          <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-pink-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Brand Claims</div>
            <div className="text-sm text-glass-secondary font-body">Review ownership requests</div>
          </div>
          {pendingClaimsCount > 0 && (
            <span className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {pendingClaimsCount}
            </span>
          )}
        </Link>

        <Link
          to="/admin/moderation"
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3 relative"
        >
          <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <div className="font-bold text-glass font-body">Moderation</div>
            <div className="text-sm text-glass-secondary font-body">Triage user reports</div>
          </div>
          {pendingReportsCount > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[24px] h-6 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {pendingReportsCount}
            </span>
          )}
        </Link>

        <button
          onClick={loadStats}
          disabled={loadingStats}
          className="glass-card-sm p-4 hover:bg-slate-700/60 transition-all flex items-center gap-3 text-left"
        >
          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
            {loadingStats ? (
              <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
            ) : (
              <Database className="w-6 h-6 text-blue-400" />
            )}
          </div>
          <div>
            <div className="font-bold text-glass font-body">Refresh Stats</div>
            <div className="text-sm text-glass-secondary font-body">Update data quality overview</div>
          </div>
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            <Database className="w-5 h-5" />
            Data Quality Overview
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-700/40 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-glass">{stats.totalItems.toLocaleString()}</div>
              <div className="text-sm text-glass-secondary">Total Items</div>
            </div>
            <div className="bg-slate-700/40 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-glass">{stats.totalBrands.toLocaleString()}</div>
              <div className="text-sm text-glass-secondary">Brands</div>
            </div>
            <div className="bg-slate-700/40 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-glass">{stats.totalUsers.toLocaleString()}</div>
              <div className="text-sm text-glass-secondary">Users</div>
            </div>
            <div className="bg-slate-700/40 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-purple-400">
                {((1 - stats.itemsMissingBrand / stats.totalItems) * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-glass-secondary">Data Completeness</div>
            </div>
          </div>

          <h3 className="font-bold text-glass-secondary mb-3 font-body">Issues Found</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className={`flex items-center justify-between p-3 rounded-lg ${stats.itemsMissingBrand > 0 ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
              <span className="flex items-center gap-2 text-glass-secondary">
                {stats.itemsMissingBrand > 0 ? (
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                )}
                Items missing brand
              </span>
              <span className={`font-bold ${stats.itemsMissingBrand > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {stats.itemsMissingBrand}
              </span>
            </div>

            <div className={`flex items-center justify-between p-3 rounded-lg ${stats.itemsMissingName > 0 ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
              <span className="flex items-center gap-2 text-glass-secondary">
                {stats.itemsMissingName > 0 ? (
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                )}
                Items missing name
              </span>
              <span className={`font-bold ${stats.itemsMissingName > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {stats.itemsMissingName}
              </span>
            </div>

            <div className={`flex items-center justify-between p-3 rounded-lg ${stats.itemsMissingReleaseInfo > 0 ? 'bg-amber-500/20' : 'bg-green-500/20'}`}>
              <span className="flex items-center gap-2 text-glass-secondary">
                {stats.itemsMissingReleaseInfo > 0 ? (
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                )}
                Items missing release info
              </span>
              <span className={`font-bold ${stats.itemsMissingReleaseInfo > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                {stats.itemsMissingReleaseInfo}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/40">
              <span className="flex items-center gap-2 text-glass-secondary">
                <Package className="w-5 h-5 text-glass-muted" />
                Items without photos
              </span>
              <span className="font-bold text-glass-secondary">
                {stats.itemsMissingPhotos}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Data Quality Tools */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
          <Search className="w-5 h-5" />
          Data Quality Tools
        </h2>

        <div className="space-y-4">
          {/* Similar Brands Tool */}
          <div className="border border-slate-600 rounded-lg overflow-hidden">
            <button
              onClick={() => {
                if (!showSimilarBrands && similarBrands.length === 0) {
                  findSimilarBrands();
                } else {
                  setShowSimilarBrands(!showSimilarBrands);
                }
              }}
              disabled={loadingSimilarBrands}
              className="w-full flex items-center justify-between p-4 bg-slate-700/40 hover:bg-slate-700/60 transition-colors"
            >
              <div className="flex items-center gap-3">
                {loadingSimilarBrands ? (
                  <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
                ) : showSimilarBrands ? (
                  <ChevronDown className="w-5 h-5 text-glass-muted" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-glass-muted" />
                )}
                <Castle className="w-5 h-5 text-purple-400" />
                <span className="font-medium text-glass">Find Similar Brand Names</span>
                {similarBrands.length > 0 && (
                  <span className="text-sm text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded-full">
                    {similarBrands.length} found
                  </span>
                )}
              </div>
              <span className="text-sm text-glass-secondary">Detect typos & duplicates</span>
            </button>
            
            {showSimilarBrands && (
              <div className="p-4 border-t border-slate-600">
                {/* Toggle dismissed */}
                {dismissedBrandPairs.size > 0 && (
                  <button
                    onClick={() => setShowDismissed(!showDismissed)}
                    className="mb-3 text-sm text-glass-muted hover:text-glass-secondary flex items-center gap-1"
                  >
                    {showDismissed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showDismissed ? 'Hide' : 'Show'} {dismissedBrandPairs.size} dismissed
                  </button>
                )}

                {similarBrands.filter(p => showDismissed || !dismissedBrandPairs.has(pairKey(p.name1, p.name2))).length === 0 ? (
                  <p className="text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    No similar brand names found! 🎉
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {similarBrands
                      .filter(pair => showDismissed || !dismissedBrandPairs.has(pairKey(pair.name1, pair.name2)))
                      .map((pair, idx) => {
                        const isDismissed = dismissedBrandPairs.has(pairKey(pair.name1, pair.name2));
                        return (
                          <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${isDismissed ? 'bg-slate-700/40 opacity-60' : 'bg-amber-500/20'}`}>
                            <div>
                              <div className="font-medium text-glass">"{pair.name1}" ({pair.count1} items)</div>
                              <div className="font-medium text-glass">"{pair.name2}" ({pair.count2} items)</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right mr-2">
                                <div className="text-sm text-amber-400">{(pair.similarity * 100).toFixed(0)}% similar</div>
                              </div>
                              {isDismissed ? (
                                <button
                                  onClick={() => undismissBrandPair(pair.name1, pair.name2)}
                                  className="p-1.5 text-glass-muted hover:text-glass-secondary hover:bg-slate-600 rounded"
                                  title="Restore"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => dismissBrandPair(pair.name1, pair.name2)}
                                    className="p-1.5 text-glass-muted hover:text-glass-secondary hover:bg-slate-600 rounded"
                                    title="Dismiss (these are OK)"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                  <Link
                                    to={`/admin/merge-brands?old=${encodeURIComponent(pair.count1! < pair.count2! ? pair.name1 : pair.name2)}&new=${encodeURIComponent(pair.count1! >= pair.count2! ? pair.name1 : pair.name2)}`}
                                    className="p-1.5 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded"
                                    title="Merge"
                                  >
                                    <Merge className="w-4 h-4" />
                                  </Link>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Similar Items Tool */}
          <div className="border border-slate-600 rounded-lg overflow-hidden">
            <button
              onClick={() => {
                if (!showSimilarItems && similarItems.length === 0) {
                  findSimilarItems();
                } else {
                  setShowSimilarItems(!showSimilarItems);
                }
              }}
              disabled={loadingSimilarItems}
              className="w-full flex items-center justify-between p-4 bg-slate-700/40 hover:bg-slate-700/60 transition-colors"
            >
              <div className="flex items-center gap-3">
                {loadingSimilarItems ? (
                  <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
                ) : showSimilarItems ? (
                  <ChevronDown className="w-5 h-5 text-glass-muted" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-glass-muted" />
                )}
                <Sparkles className="w-5 h-5 text-pink-400" />
                <span className="font-medium text-glass">Find Similar Item Names</span>
                {similarItems.length > 0 && (() => {
                  // Count VISIBLE (non-dismissed) pairs so the badge shrinks
                  // as pairs are dismissed. `total` is what the scan produced;
                  // shown parenthetically when dismissals hide some.
                  const total = similarItems.reduce((acc, b) => acc + b.pairs.length, 0);
                  const visible = similarItems.reduce((acc, b) => acc + b.pairs.filter(
                    p => !dismissedItemPairs.has(pairKey(p.name1, p.name2, b.brand))
                  ).length, 0);
                  const hidden = total - visible;
                  return (
                    <span className="text-sm text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded-full">
                      {visible} found{hidden > 0 && ` (${hidden} dismissed)`}
                    </span>
                  );
                })()}
              </div>
              <span className="text-sm text-glass-secondary">Within each brand</span>
            </button>
            
            {showSimilarItems && (
              <div className="p-4 border-t border-slate-600 max-h-96 overflow-y-auto">
                {/* Toggle to reveal previously-dismissed pairs. Mirrors the
                    Similar Brand Names panel above — gives visible proof
                    that the dismissal store is loaded and working. */}
                {dismissedItemPairs.size > 0 && (
                  <button
                    onClick={() => setShowDismissed(!showDismissed)}
                    className="mb-3 text-sm text-glass-muted hover:text-glass-secondary flex items-center gap-1"
                  >
                    {showDismissed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showDismissed ? 'Hide' : 'Show'} {dismissedItemPairs.size} dismissed
                  </button>
                )}

                {/* Sticky bulk-action bar. Appears once similarItems has any
                    visible pairs. When >0 are selected, the Dismiss button
                    activates and can commit them in batched Firestore writes. */}
                {(() => {
                  const visiblePairKeys: string[] = [];
                  for (const bg of similarItems) {
                    for (const p of bg.pairs) {
                      const k = pairKey(p.name1, p.name2, bg.brand);
                      if (!dismissedItemPairs.has(k)) visiblePairKeys.push(k);
                    }
                  }
                  if (visiblePairKeys.length === 0) return null;
                  const allVisibleSelected = visiblePairKeys.every(k => selectedItemPairs.has(k));
                  const selectedCount = selectedItemPairs.size;
                  return (
                    <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-3 px-4 py-2 bg-slate-800 border-b border-slate-600 flex items-center justify-between gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          className="w-4 h-4 rounded accent-purple-500"
                        />
                        <span className="text-sm text-glass-secondary font-body">
                          {allVisibleSelected ? 'Deselect all' : 'Select all visible'} ({visiblePairKeys.length})
                        </span>
                      </label>
                      {selectedCount > 0 && (
                        <button
                          onClick={bulkDismissSelected}
                          disabled={isBulkDismissing}
                          className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded font-body disabled:opacity-60"
                        >
                          {isBulkDismissing
                            ? bulkProgress
                              ? `Dismissing ${bulkProgress.done}/${bulkProgress.total}…`
                              : 'Dismissing…'
                            : `Dismiss ${selectedCount} selected`}
                        </button>
                      )}
                    </div>
                  );
                })()}

                {similarItems.length === 0 ? (
                  <p className="text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    No similar item names found! 🎉
                  </p>
                ) : (
                  <div className="space-y-4">
                    {similarItems.map((brandGroup, idx) => {
                      const visiblePairs = brandGroup.pairs.filter(
                        pair => showDismissed || !dismissedItemPairs.has(pairKey(pair.name1, pair.name2, brandGroup.brand))
                      );

                      if (visiblePairs.length === 0) return null;

                      // Per-brand-group "Select all" checkbox state.
                      const groupVisibleKeys = brandGroup.pairs
                        .filter(p => !dismissedItemPairs.has(pairKey(p.name1, p.name2, brandGroup.brand)))
                        .map(p => pairKey(p.name1, p.name2, brandGroup.brand));
                      const groupAllSelected = groupVisibleKeys.length > 0
                        && groupVisibleKeys.every(k => selectedItemPairs.has(k));

                      return (
                        <div key={idx} className="border border-slate-600 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-bold text-glass">{brandGroup.brand}</div>
                            {groupVisibleKeys.length > 0 && (
                              <label className="flex items-center gap-2 cursor-pointer text-xs text-glass-muted hover:text-glass-secondary">
                                <input
                                  type="checkbox"
                                  checked={groupAllSelected}
                                  onChange={() => toggleSelectAllInBrandGroup(brandGroup)}
                                  className="w-3.5 h-3.5 rounded accent-purple-500"
                                />
                                Select all ({groupVisibleKeys.length})
                              </label>
                            )}
                          </div>
                          <div className="space-y-1">
                            {visiblePairs.map((pair, pIdx) => {
                              const thisKey = pairKey(pair.name1, pair.name2, brandGroup.brand);
                              const isDismissed = dismissedItemPairs.has(thisKey);
                              const isSelected = selectedItemPairs.has(thisKey);
                              return (
                                <div key={pIdx} className={`text-sm flex items-center justify-between p-2 rounded ${isDismissed ? 'bg-slate-700/40 opacity-60' : isSelected ? 'bg-purple-500/25' : 'bg-amber-500/20'}`}>
                                  {!isDismissed && (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleItemPairSelection(brandGroup.brand, pair.name1, pair.name2)}
                                      className="w-4 h-4 mr-3 rounded accent-purple-500 flex-shrink-0"
                                    />
                                  )}
                                  <span className="flex-1 text-glass-secondary">"{pair.name1}" ({pair.count1}) ↔ "{pair.name2}" ({pair.count2})</span>
                                  <div className="flex items-center gap-1 ml-2">
                                    <span className="text-amber-400 mr-2">{(pair.similarity * 100).toFixed(0)}%</span>
                                    {isDismissed ? (
                                      <button
                                        onClick={() => undismissItemPair(brandGroup.brand, pair.name1, pair.name2)}
                                        className="p-1 text-glass-muted hover:text-glass-secondary hover:bg-slate-600 rounded"
                                        title="Restore"
                                      >
                                        <RefreshCw className="w-3.5 h-3.5" />
                                      </button>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => dismissItemPair(brandGroup.brand, pair.name1, pair.name2)}
                                          className="p-1 text-glass-muted hover:text-glass-secondary hover:bg-slate-600 rounded"
                                          title="Dismiss (these are OK)"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={() => openMergeItemsModal(brandGroup.brand, pair.name1, pair.name2, pair.count1 || 0, pair.count2 || 0)}
                                          className="p-1 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded"
                                          title="Merge"
                                        >
                                          <Merge className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Catalog Merge Candidates — sort by community confidence (lowest first) */}
          <div className="border border-slate-600 rounded-lg overflow-hidden">
            <button
              onClick={() => {
                if (!showCatalogCandidates && catalogCandidates.length === 0) {
                  findCatalogCandidates();
                } else {
                  setShowCatalogCandidates(!showCatalogCandidates);
                }
              }}
              disabled={loadingCatalogCandidates}
              className="w-full flex items-center justify-between p-4 bg-slate-700/40 hover:bg-slate-700/60 transition-colors"
            >
              <div className="flex items-center gap-3">
                {loadingCatalogCandidates ? (
                  <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
                ) : showCatalogCandidates ? (
                  <ChevronDown className="w-5 h-5 text-glass-muted" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-glass-muted" />
                )}
                <Database className="w-5 h-5 text-emerald-400" />
                <span className="font-medium text-glass">Catalog merge candidates</span>
                {catalogCandidates.length > 0 && (
                  <span className="text-sm text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded-full">
                    {catalogCandidates.length} shown
                  </span>
                )}
              </div>
              <span className="text-sm text-glass-secondary">Sort by confidence (lowest first)</span>
            </button>

            {showCatalogCandidates && (
              <div className="p-4 border-t border-slate-600 max-h-96 overflow-y-auto">
                {catalogCandidates.length === 0 ? (
                  <p className="text-glass-secondary">No catalog entries to show.</p>
                ) : (
                  <div className="space-y-1">
                    {catalogCandidates.map((c) => (
                      <div
                        key={c.slug}
                        className="flex items-center justify-between p-2 rounded bg-slate-700/30 hover:bg-slate-700/50"
                      >
                        <div className="flex-1 min-w-0 mr-3">
                          <div className="text-sm text-glass truncate">
                            <strong>{c.brand}</strong> — {c.name}
                          </div>
                          <div className="text-xs text-glass-muted">
                            {c.collectorCount} {c.collectorCount === 1 ? 'collector' : 'collectors'} ·{' '}
                            <span className={c.confirmCount > 0 ? 'text-emerald-400' : 'text-amber-300'}>
                              {c.confirmCount > 0 ? `✓ ${c.confirmCount} confirmed` : 'unconfirmed'}
                            </span>
                          </div>
                        </div>
                        <Link
                          to={`/admin/merge-polishes?fromBrand=${encodeURIComponent(c.brand)}&fromName=${encodeURIComponent(c.name)}`}
                          className="px-2 py-1 text-xs bg-purple-600/40 text-purple-200 hover:bg-purple-600/60 rounded inline-flex items-center gap-1"
                          title="Open MergePolishes pre-filled with this polish as the source"
                        >
                          <Merge className="w-3.5 h-3.5" />
                          Merge…
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Missing Fields Tool */}
          <div className="border border-slate-600 rounded-lg overflow-hidden">
            <button
              onClick={() => {
                if (!showMissingFields && missingFieldItems.length === 0) {
                  findMissingFields();
                } else {
                  setShowMissingFields(!showMissingFields);
                }
              }}
              disabled={loadingMissingFields}
              className="w-full flex items-center justify-between p-4 bg-slate-700/40 hover:bg-slate-700/60 transition-colors"
            >
              <div className="flex items-center gap-3">
                {loadingMissingFields ? (
                  <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
                ) : showMissingFields ? (
                  <ChevronDown className="w-5 h-5 text-glass-muted" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-glass-muted" />
                )}
                <FileWarning className="w-5 h-5 text-red-400" />
                <span className="font-medium text-glass">Items Missing Key Fields</span>
                {missingFieldItems.length > 0 && (
                  <span className="text-sm text-red-300 bg-red-500/20 px-2 py-0.5 rounded-full">
                    {missingFieldItems.length} found
                  </span>
                )}
              </div>
              <span className="text-sm text-glass-secondary">Brand or name missing</span>
            </button>

            {showMissingFields && (
              <div className="p-4 border-t border-slate-600 max-h-64 overflow-y-auto">
                {missingFieldItems.length === 0 ? (
                  <p className="text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    All items have required fields! 🎉
                  </p>
                ) : (
                  <div className="space-y-2">
                    {missingFieldItems.slice(0, 50).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-red-500/20 rounded-lg text-sm">
                        <div>
                          <span className="font-medium text-glass">{item.name}</span>
                          <span className="text-glass-secondary"> by {item.brand}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-red-400">
                            Missing: {item.missingFields.join(', ')}
                          </span>
                        </div>
                      </div>
                    ))}
                    {missingFieldItems.length > 50 && (
                      <p className="text-glass-muted text-sm text-center">
                        ... and {missingFieldItems.length - 50} more
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Typography backfill — one-shot rewrite of smart quotes / dashes
          across all items + brands + polishCatalog. Same class as the
          other backfill Cloud Functions (backfillBrandLower, etc.);
          exposed here as a button because it's the kind of thing you
          only need to run once after the write-side normalizer ships. */}
      <div className="border border-slate-600 rounded-xl p-4 mb-4 bg-slate-800/40">
        <h3 className="font-bold text-glass mb-1 font-body flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-400" />
          Typography Backfill
        </h3>
        <p className="text-sm text-glass-secondary mb-3 font-body">
          Rewrites smart quotes (<code>’ ‘</code>), curly quotes (<code>” “</code>), typographic dashes (<code>– —</code>), and ellipsis (<code>…</code>) to plain ASCII across every item, brand, and polishCatalog doc. Idempotent — running it twice does nothing on the second pass. Run once after deploying the write-side normalizer; then Find Similar Item Names stops surfacing typography-only duplicates.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => runTypographyBackfill(true)}
            disabled={typographyBackfillLoading !== 'idle'}
            className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-glass rounded font-body disabled:opacity-60"
          >
            {typographyBackfillLoading === 'dry' ? 'Scanning…' : 'Dry run'}
          </button>
          <button
            onClick={() => runTypographyBackfill(false)}
            disabled={typographyBackfillLoading !== 'idle'}
            className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded font-body disabled:opacity-60"
          >
            {typographyBackfillLoading === 'real' ? 'Running…' : 'Apply for real'}
          </button>
        </div>
        {typographyBackfillResult && (
          <pre className="mt-3 text-xs text-glass-secondary bg-slate-900/60 p-3 rounded overflow-x-auto max-h-96 font-mono">
{JSON.stringify(typographyBackfillResult, null, 2)}
          </pre>
        )}
      </div>

      {/* Item currency backfill — one-shot stamp of priceCurrency on every
          user item that has a `price` but no explicit currency. Ships with
          the per-item currency picker to lock in historical purchase
          currencies before any owner changes their profile currency. */}
      <div className="border border-slate-600 rounded-xl p-4 mb-4 bg-slate-800/40">
        <h3 className="font-bold text-glass mb-1 font-body flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-400" />
          Item Currency Backfill
        </h3>
        <p className="text-sm text-glass-secondary mb-3 font-body">
          Stamps <code>priceCurrency</code> = the owner's current profile currency on every user item that has a <code>price</code> but no per-item currency yet. Locks in historical purchase currencies so a user changing their profile currency later doesn't re-label old items with the new symbol. Idempotent — running twice does nothing on the second pass.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => runCurrencyBackfill(true)}
            disabled={currencyBackfillLoading !== 'idle'}
            className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-glass rounded font-body disabled:opacity-60"
          >
            {currencyBackfillLoading === 'dry' ? 'Scanning…' : 'Dry run'}
          </button>
          <button
            onClick={() => runCurrencyBackfill(false)}
            disabled={currencyBackfillLoading !== 'idle'}
            className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded font-body disabled:opacity-60"
          >
            {currencyBackfillLoading === 'real' ? 'Running…' : 'Apply for real'}
          </button>
        </div>
        {currencyBackfillResult && (
          <pre className="mt-3 text-xs text-glass-secondary bg-slate-900/60 p-3 rounded overflow-x-auto max-h-96 font-mono">
{JSON.stringify(currencyBackfillResult, null, 2)}
          </pre>
        )}
      </div>

      {/* Tips */}
      <div className="bg-blue-500/20 border border-blue-500/50 rounded-xl p-4">
        <h3 className="font-bold text-blue-300 mb-2 font-body">💡 Maintenance Tips</h3>
        <ul className="text-blue-200 text-sm space-y-1 font-body">
          <li>• Run "Find Similar Brand Names" periodically to catch typos</li>
          <li>• Use "Merge Brands" to combine duplicates (keeps the correct spelling)</li>
          <li>• Click ✕ to dismiss pairs that are intentionally different</li>
          <li>• Check "Items Missing Key Fields" after imports</li>
          <li>• Re-run "Populate Brands" after merges to update stats</li>
        </ul>
      </div>

      {/* Merge Items Modal */}
      {showMergeItemsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-glass flex items-center gap-2 font-heading">
                <Merge className="w-6 h-6 text-purple-400" />
                Merge Item Names
              </h3>
              <button
                onClick={() => setShowMergeItemsModal(false)}
                className="text-glass-muted hover:text-glass-secondary"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-3">
                <div className="text-sm text-purple-300 font-medium">Brand</div>
                <div className="text-purple-200">{mergeItemBrand}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  ❌ Rename FROM (will be removed)
                </label>
                <input
                  type="text"
                  value={mergeItemOld}
                  onChange={(e) => setMergeItemOld(e.target.value)}
                  className="input-glass"
                />
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    // Swap FROM/TO. Handy when the auto-guess got them backwards
                    // (e.g. the "less-used" variant is actually the canonical one).
                    const oldVal = mergeItemOld;
                    setMergeItemOld(mergeItemNew);
                    setMergeItemNew(oldVal);
                  }}
                  className="p-2 rounded-full bg-slate-700 hover:bg-slate-600 text-glass-secondary hover:text-glass transition-colors"
                  title="Swap FROM and TO"
                >
                  <ArrowUpDown className="w-4 h-4" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  ✅ Rename TO (correct name)
                </label>
                <input
                  type="text"
                  value={mergeItemNew}
                  onChange={(e) => setMergeItemNew(e.target.value)}
                  className="input-glass"
                />
              </div>

              {mergeItemStatus && (
                <div className={`p-3 rounded-lg ${
                  mergeItemStatus.includes('✅') ? 'bg-green-500/20 text-green-300' :
                  mergeItemStatus.includes('❌') ? 'bg-red-500/20 text-red-300' :
                  'bg-blue-500/20 text-blue-300'
                }`}>
                  {mergeItemStatus}
                </div>
              )}

              <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-3 text-sm text-amber-200 font-body">
                <strong className="text-amber-100">⚠️ Note:</strong> This will rename all items with the "FROM" name to the "TO" name across all users' collections for this brand.
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowMergeItemsModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-600 text-glass-secondary rounded-lg hover:bg-slate-700/50 font-body"
                >
                  Cancel
                </button>
                <button
                  onClick={mergeItems}
                  disabled={isMergingItems || !mergeItemOld || !mergeItemNew}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 font-body"
                >
                  {isMergingItems ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Merging...
                    </>
                  ) : (
                    <>
                      <Merge className="w-4 h-4" />
                      Merge Items
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
